// The map inset of a NEEDS YOU item (docs/design/inbox.md §8, R1846; the
// Impeccable comp C of the INBOX).
//
// A small square of the chart round the item's cell, at the right of the
// item: the water, the ridges as contour fragments, the trench edges, the
// pin with the number of the item and the place name under it. It is an
// inline SVG, written as markup like the ridge profile of
// `ui/cards/terrain.ts`: no canvas and no frame loop (§10.3).
//
// **THE CONTOURS ARE HEX EDGES.** The chart draws the hexes and not a
// smoothed coast (`render/hex-ring.ts`). The rim of a ridge is every edge
// between a ridge cell and a cell that is no ridge; its second ring is
// every edge between a ridge cell with six ridge neighbours and one with
// fewer, as the second contour ring of the terrain steps in; a trench
// edge is dashed. The SVG viewport clips what lies past the square.
//
// **COST.** The terrain part walks about 150 cells of the window and their
// six neighbours, once per cell and blocked mask: the mask does not change
// inside a season, and a new hello brings a new mask, so the cache keys on
// the mask itself. The pin and the name are a few bytes of markup per
// call. The inset draws no owner tint: the owners live in the territory
// layer and change with every flip, and a tint would cost the cache.

import { f1 } from '../../util/num';
import { CHART } from '../../gen/presentation';
import type { InboxMsg } from '../../gen/proto/aquaphobia';
import {
  BLOCK_RIDGE, BLOCK_TRENCH, COLS, COL_PITCH, HEX_SIZE, ROWS, ROW_PITCH, cellCenter, neighborAt,
} from '../../world';
import { esc } from '../esc';
import type { GameState } from '../../state';
import { cellLabel, placeText } from './words';

/** The figures of the inset: the `inset_` keys of the pins' table. */
const T = CHART.pieces.inbox_pins;

/** The terrain part of each cell's inset, kept per blocked mask. */
const cache = new WeakMap<Uint8Array, Map<number, string>>();

/** The place an inset names: the place the server named for the message
 *  (R3132), its object, or the label of its cell, the one cell label of
 *  the INBOX (`words.ts`, inbox.md §3.1). */
export function placeOf(m: InboxMsg, s: GameState): string {
  return placeText(m.params, s) || m.params.site || cellLabel(m.cell);
}

/** A name cut to `n` characters, with an ellipsis for what it lost. */
export function clipName(name: string, n: number): string {
  const chars = Array.from(name);
  return chars.length <= n ? name : `${chars.slice(0, Math.max(1, n - 1)).join('')}…`;
}

/**
 * The contour fragments round one cell, as three SVG paths in inset px.
 * The answer is kept per cell for the life of the blocked mask.
 */
export function insetTerrain(blocked: Uint8Array, cell: number): string {
  let byCell = cache.get(blocked);
  if (!byCell) { byCell = new Map(); cache.set(blocked, byCell); }
  const had = byCell.get(cell);
  if (had !== undefined) return had;
  const svg = buildTerrain(blocked, cell);
  byCell.set(cell, svg);
  return svg;
}

/** Walks the window round the cell and writes its three paths. */
function buildTerrain(blocked: Uint8Array, cell: number): string {
  const px = T.fig.inset_px;
  const half = T.fig.inset_cells * COL_PITCH;
  const s = px / (2 * half);
  const [cx, cy] = cellCenter(cell);
  const ox = cx - half, oy = cy - half;
  const row0 = Math.floor(cell / COLS), col0 = cell % COLS;
  const kc = Math.ceil(half / COL_PITCH) + 1;
  const kr = Math.ceil(half / ROW_PITCH) + 1;
  const at = (i: number): number => (i >= 0 && i < blocked.length ? blocked[i] : 0);
  // A ridge cell whose six neighbours are ridge: the second ring stands
  // round these. A cell past the edge of the grid counts as ridge, so the
  // edge of the world draws no ring.
  const inner = (i: number): boolean => {
    if (at(i) !== BLOCK_RIDGE) return false;
    for (let k = 0; k < 6; k++) {
      const n = neighborAt(i, k);
      if (n >= 0 && at(n) !== BLOCK_RIDGE) return false;
    }
    return true;
  };
  let rim = '', ring = '', trench = '';
  for (let r = Math.max(0, row0 - kr); r <= Math.min(ROWS - 1, row0 + kr); r++) {
    for (let c = Math.max(0, col0 - kc); c <= Math.min(COLS - 1, col0 + kc); c++) {
      const i = r * COLS + c;
      const kind = at(i);
      if (kind !== BLOCK_RIDGE && kind !== BLOCK_TRENCH) continue;
      const iInner = kind === BLOCK_RIDGE && inner(i);
      const [x, y] = cellCenter(i);
      for (let k = 0; k < 6; k++) {
        const n = neighborAt(i, k);
        if (n < 0) continue;
        const nk = at(n);
        let edge: 'rim' | 'ring' | 'trench' | null = null;
        if (kind === BLOCK_RIDGE && nk !== BLOCK_RIDGE) edge = 'rim';
        else if (kind === BLOCK_RIDGE && iInner && !inner(n)) edge = 'ring';
        else if (kind === BLOCK_TRENCH && nk !== BLOCK_TRENCH && nk !== BLOCK_RIDGE) edge = 'trench';
        if (edge === null) continue;
        const d = sideOf(x, y, n, ox, oy, s, px);
        if (d === '') continue;
        if (edge === 'rim') rim += d;
        else if (edge === 'ring') ring += d;
        else trench += d;
      }
    }
  }
  const dash = T.arr.inset_trench_dash.join(' ');
  return (rim ? `<path class="ny-c1" d="${rim}"/>` : '')
    + (ring ? `<path class="ny-c2" d="${ring}"/>` : '')
    + (trench ? `<path class="ny-tr" d="${trench}" stroke-dasharray="${dash}"/>` : '');
}

/**
 * The hex side of the cell at `x, y` that faces the cell `n`, as one
 * `M … L …` run in inset px, or '' when the side lies outside the square.
 * The hex is pointy-top, so the side that faces the angle θ runs between
 * the corners at θ − 30° and θ + 30°.
 */
function sideOf(
  x: number, y: number, n: number, ox: number, oy: number, s: number, px: number,
): string {
  const [nx, ny] = cellCenter(n);
  const theta = Math.round(Math.atan2(ny - y, nx - x) / (Math.PI / 3)) * (Math.PI / 3);
  const ax = (x + HEX_SIZE * Math.cos(theta - Math.PI / 6) - ox) * s;
  const ay = (y + HEX_SIZE * Math.sin(theta - Math.PI / 6) - oy) * s;
  const bx = (x + HEX_SIZE * Math.cos(theta + Math.PI / 6) - ox) * s;
  const by = (y + HEX_SIZE * Math.sin(theta + Math.PI / 6) - oy) * s;
  const out = (v: number): boolean => v < 0 || v > px;
  if ((out(ax) || out(ay)) && (out(bx) || out(by))) return '';
  return `M${f1(ax)} ${f1(ay)}L${f1(bx)} ${f1(by)}`;
}

/**
 * The whole inset of one item: the water, the contours, the pin with its
 * number at the centre, and the place name under it. A world before its
 * hello has no mask, and the inset then draws the water and the pin alone.
 */
export function insetSvg(blocked: Uint8Array | null, cell: number, num: number, place: string): string {
  const px = T.fig.inset_px;
  const mid = f1(px / 2);
  const terrain = blocked ? insetTerrain(blocked, cell) : '';
  const name = esc(clipName(place, T.fig.inset_name_chars));
  return `<svg class="ny-map" viewBox="0 0 ${px} ${px}" width="${px}" height="${px}"`
    + ' aria-hidden="true" focusable="false">'
    + `<rect class="ny-w" width="${px}" height="${px}"/>${terrain}`
    + `<circle class="ny-pin" cx="${mid}" cy="${mid}" r="${T.fig.inset_pin_r}"/>`
    + `<text class="ny-pn" x="${mid}" y="${mid}">${num}</text>`
    + `<text class="ny-pl" x="${mid}" y="${f1(px - 3)}">${name}</text></svg>`;
}
